import { Injectable } from '@angular/core';
import { Class } from '../../models/interfaces/module-class.interface';
import { Module } from '../../models/interfaces/module.interface';
import { Api } from '../api/api';
import { createCrudUrl, CrudRequestParams } from '../pagination/models/interfaces/crud';
import { environment } from '../../../../environments/environment';
import { PaginationDto } from '../pagination/models/interfaces/pagination-dto';
import { getCrudErrors } from '../../utils/functions';
import { moduleClassMock, moduleMock } from '../../mocks/interfaces/module.mock';
import { courseMock } from '../../mocks/interfaces/course.mock';
import { applyMockCrudParams } from '../../utils/mock-crud.util';

export interface CourseModuleClass extends Class {
  moduleId: string;
  moduleOrder: number;
  courseId: string;
}

@Injectable({
  providedIn: 'root',
})
export class ModuleService {
  private readonly api: Api = new Api();

  private modules: Module[] = structuredClone(moduleMock);

  private classes: Class[] = structuredClone(moduleClassMock);

  private courses = structuredClone(courseMock);

  //#region Real API

  public async getAll(): Promise<Module[]> {
    if (environment.useFakeBackend) {
      return this.getAllMocked();
    }

    const url = environment.routes.modules.base;
    const { error, success } = await this.api.getAsync<Module[]>(url);

    if (!success)
      throw new Error(getCrudErrors(error)[0]);

    return success;
  }

  public async search(params: CrudRequestParams<Module>): Promise<Module[]> {
    if (environment.useFakeBackend) {
      return this.searchMocked(params);
    }

    const url = createCrudUrl<Module>(environment.routes.modules.search, params);
    const { error, success } = await this.api.getAsync<PaginationDto<Module>>(url);

    if (!success)
      throw new Error(getCrudErrors(error)[0]);

    return success.data;
  }

  public async getByCourseId(courseId: string, params: CrudRequestParams<Module> = {}): Promise<Module[]> {
    if (environment.useFakeBackend) {
      return this.getByCourseIdMocked(courseId, params);
    }

    const searchParams = params.search as Record<string, unknown> | undefined;

    return this.search({
      ...params,
      search: {
        ...searchParams,
        courseId: { $eq: courseId },
      } as any,
      sort: params.sort ?? [{ field: 'order', order: 'ASC' }],
    });
  }

  public async getByIds(moduleIds: string[]): Promise<Module[]> {
    if (environment.useFakeBackend) {
      return this.getByIdsMocked(moduleIds);
    }

    if (!moduleIds.length) {
      return [];
    }

    return this.search({
      search: { id: { $in: moduleIds } },
      sort: [{ field: 'order', order: 'ASC' }],
    });
  }

  public async getClassesByModuleIds(moduleIds: string[]): Promise<CourseModuleClass[]> {
    if (environment.useFakeBackend) {
      return this.getClassesByModuleIdsMocked(moduleIds);
    }

    if (!moduleIds.length) {
      return [];
    }

    const params = new URLSearchParams({ moduleIds: moduleIds.join(',') });
    const url = `${ environment.routes.modules.classesByModuleIds }?${ params.toString() }`;
    const { error, success } = await this.api.getAsync<CourseModuleClass[]>(url);

    if (!success)
      throw new Error(getCrudErrors(error)[0]);

    return success;
  }

  //#endregion

  //#region Fake Backend

  public async getAllMocked(): Promise<Module[]> {
    return [...this.modules];
  }

  public async searchMocked(params: CrudRequestParams<Module> = {}): Promise<Module[]> {
    return applyMockCrudParams(this.modules, params);
  }

  public async getByCourseIdMocked(courseId: string, params: CrudRequestParams<Module> = {}): Promise<Module[]> {
    const byCourse = applyMockCrudParams(this.modules, {
      ...params,
      search: {
        ...(params.search as Record<string, unknown> | undefined),
        courseId: { $eq: courseId },
      } as any,
      sort: params.sort ?? [{ field: 'order', order: 'ASC' }],
    });

    if (byCourse.length) {
      return byCourse;
    }

    const course = this.courses.find(item => item.id === courseId);
    if (!course?.modulesIds?.length) {
      return [];
    }

    return applyMockCrudParams(
      this.modules.filter(module => course.modulesIds!.includes(module.id)),
      {
        ...params,
        sort: params.sort ?? [{ field: 'order', order: 'ASC' }],
      },
    );
  }

  public async getByIdsMocked(moduleIds: string[]): Promise<Module[]> {
    if (!moduleIds.length) {
      return [];
    }

    return this.searchMocked({
      search: { id: { $in: moduleIds } },
      sort: [{ field: 'order', order: 'ASC' }],
    });
  }

  public async getClassesByModuleIdsMocked(moduleIds: string[]): Promise<CourseModuleClass[]> {
    if (!moduleIds.length) {
      return [];
    }

    const moduleById = new Map(this.modules.map(module => [module.id, module]));

    return this.classes
      .filter(moduleClass => moduleIds.includes(moduleClass.moduleId))
      .map(moduleClass => {
        const module = moduleById.get(moduleClass.moduleId);

        return {
          ...moduleClass,
          moduleId: moduleClass.moduleId,
          moduleOrder: module?.order ?? 0,
          courseId: module?.courseId ?? '',
        };
      })
      .sort((first, second) => {
        if (first.moduleOrder !== second.moduleOrder) {
          return first.moduleOrder - second.moduleOrder;
        }

        return first.order - second.order;
      });
  }

  public resetMockData(): void {
    this.modules = structuredClone(moduleMock);
    this.classes = structuredClone(moduleClassMock);
    this.courses = structuredClone(courseMock);
  }

  //#endregion
}
